import React, { useEffect, useState } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { routineService } from "../../services/api";
import { newRequestId } from "../../utils/requestId";
import RoutineBriefings from "./RoutineBriefings";

const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const localInput = (value) => {
  const date = new Date(value || Date.now());
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
const display = (value, timezone) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(new Date(value));
function Field({ label, children }) {
  return (
    <label className="block text-xs font-semibold text-[#58635d]">
      {label}
      {children}
    </label>
  );
}

function RoutineForm({ initial, today, timezone, busy, save, close }) {
  const defaults = {
    title: "",
    kind: "training",
    goal: "",
    plan: "",
    preferences: "",
    weekdays: [],
    local_time: "18:00",
    duration_minutes: 60,
    start_date: today,
    end_date: null,
    lead_minutes: 60,
    baseline_count: 0,
    baseline_as_of: null,
    target_count: null,
    milestones: [],
    enabled: true,
  };
  const [values, setValues] = useState(() =>
    Object.fromEntries(
      Object.entries(defaults).map(([key, value]) => [
        key,
        initial?.[key] ?? value,
      ]),
    ),
  );
  const [requestId] = useState(newRequestId);
  const set = (key, value) => setValues((old) => ({ ...old, [key]: value }));
  const field = (key, label, type = "text", extra = {}) => (
    <Field key={key} label={label}>
      {type === "textarea" ? (
        <textarea
          className={input}
          rows={3}
          value={values[key]}
          onChange={(e) => set(key, e.target.value)}
          {...extra}
        />
      ) : (
        <input
          className={input}
          type={type}
          value={values[key] ?? ""}
          onInput={
            ["date", "time"].includes(type)
              ? (e) => set(key, e.target.value || null)
              : undefined
          }
          onChange={(e) =>
            set(
              key,
              type === "number"
                ? e.target.value === ""
                  ? null
                  : Number(e.target.value)
                : e.target.value || null,
            )
          }
          {...extra}
        />
      )}
    </Field>
  );
  return (
    <form
      className="surface-panel p-5 space-y-4"
      aria-label="Routine details"
      onSubmit={(e) => {
        e.preventDefault();
        save({ ...values, ...(!initial ? { request_id: requestId } : {}) });
      }}
    >
      <h3 className="font-bold">{initial ? "Edit routine" : "Add routine"}</h3>
      {field("title", "Routine name", "text", {
        required: true,
        maxLength: 180,
        autoFocus: true,
      })}
      <Field label="Routine type">
        <select
          className={input}
          value={values.kind}
          onChange={(e) => set("kind", e.target.value)}
        >
          {[
            ["training", "Training / class"],
            ["meal", "Meal"],
            ["recovery", "Rest / recovery"],
            ["wellbeing", "Wellbeing"],
          ].map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      {field("goal", "Goal", "textarea", { maxLength: 3000 })}
      {field("plan", "Planned activity or meal", "textarea", {
        maxLength: 5000,
      })}
      {field("preferences", "Preferences and constraints", "textarea", {
        maxLength: 3000,
      })}
      <fieldset>
        <legend className="text-xs font-semibold">Scheduled weekdays</legend>
        <div className="mt-2 flex flex-wrap gap-3">
          {days.map((day, index) => (
            <label key={day} className="flex gap-1 text-xs">
              <input
                type="checkbox"
                checked={values.weekdays.includes(index)}
                onChange={(e) =>
                  set(
                    "weekdays",
                    e.target.checked
                      ? [...values.weekdays, index].sort()
                      : values.weekdays.filter((value) => value !== index),
                  )
                }
              />
              {day}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        {field("local_time", `Routine time (${timezone})`, "time", {
          required: true,
        })}
        {field("duration_minutes", "Scheduled duration (minutes)", "number", {
          min: 1,
          max: 1440,
          required: true,
        })}
        {field("start_date", "Schedule start date", "date", { required: true })}
        {field("end_date", "Schedule end date (optional)", "date")}
        {field(
          "lead_minutes",
          "Preparation reminder (minutes ahead)",
          "number",
          { min: 0, max: 1440, required: true },
        )}
        {field(
          "target_count",
          "Goal: completed sessions (optional)",
          "number",
          { min: 1, max: 1000000 },
        )}
        {field("baseline_count", "Previously completed sessions", "number", {
          min: 0,
          max: 100000,
          required: true,
        })}
        {field("baseline_as_of", "Previous count as-of date", "date", {
          required: values.baseline_count > 0,
          max: today,
        })}
      </div>
      <p className="text-xs text-[#68746d]">
        A previous count is your reported history through its as-of date. New
        logs must be after that date. Scheduling or skipping a session never
        increases progress.
      </p>
      <div className="space-y-3">
        <h4 className="text-sm font-bold">Your progress milestones</h4>
        <p className="text-xs text-[#68746d]">
          Record your own or your coach’s milestones. These notes do not
          prescribe a training or diet plan.
        </p>
        {values.milestones.map((item, index) => (
          <div
            className="rounded-lg border border-[#dce3dd] p-3 space-y-2"
            key={index}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={`Milestone ${index + 1} session count`}>
                <input
                  className={input}
                  type="number"
                  required
                  min={1}
                  max={100000}
                  value={item.after_sessions ?? ""}
                  onChange={(e) =>
                    set(
                      "milestones",
                      values.milestones.map((row, i) =>
                        i === index
                          ? { ...row, after_sessions: Number(e.target.value) }
                          : row,
                      ),
                    )
                  }
                />
              </Field>
              <Field label={`Milestone ${index + 1} title`}>
                <input
                  className={input}
                  required
                  maxLength={180}
                  value={item.title}
                  onChange={(e) =>
                    set(
                      "milestones",
                      values.milestones.map((row, i) =>
                        i === index ? { ...row, title: e.target.value } : row,
                      ),
                    )
                  }
                />
              </Field>
            </div>
            <Field label={`Milestone ${index + 1} notes`}>
              <textarea
                className={input}
                maxLength={2000}
                value={item.notes}
                onChange={(e) =>
                  set(
                    "milestones",
                    values.milestones.map((row, i) =>
                      i === index ? { ...row, notes: e.target.value } : row,
                    ),
                  )
                }
              />
            </Field>
            <button
              type="button"
              className="quiet-button text-xs"
              onClick={() =>
                set(
                  "milestones",
                  values.milestones.filter((_, i) => i !== index),
                )
              }
            >
              Remove milestone {index + 1}
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={values.milestones.length >= 20}
          className="secondary-button text-xs"
          onClick={() =>
            set("milestones", [
              ...values.milestones,
              { after_sessions: null, title: "", notes: "" },
            ])
          }
        >
          Add milestone
        </button>
      </div>
      <label className="flex gap-2 text-xs">
        <input
          type="checkbox"
          checked={values.enabled}
          onChange={(e) => set("enabled", e.target.checked)}
        />
        Routine enabled
      </label>
      <p className="text-xs text-[#68746d]">
        Pausing cancels open planned occurrences and keeps actual activity
        history. Future schedule edits preserve completed and skipped sessions.
      </p>
      <div className="flex gap-2">
        <button disabled={busy} className="primary-button">
          Save routine
        </button>
        <button
          type="button"
          disabled={busy}
          className="secondary-button"
          onClick={close}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function ActivityForm({ routine, occurrence, busy, save, close }) {
  const [requestId] = useState(newRequestId);
  const [values, setValues] = useState({
    occurred_at: localInput(occurrence?.starts_at),
    minutes: null,
    stage: "",
    effort: null,
    notes: "",
  });
  const set = (key, value) => setValues((old) => ({ ...old, [key]: value }));
  return (
    <form
      className="surface-panel p-5 space-y-3"
      aria-label="Actual activity"
      onSubmit={(e) => {
        e.preventDefault();
        save({
          ...values,
          occurred_at: new Date(values.occurred_at).toISOString(),
          routine_id: routine.id,
          occurrence_id: occurrence?.id || null,
          request_id: requestId,
        });
      }}
    >
      <h3 className="font-bold">Record actual activity · {routine.title}</h3>
      <p className="text-xs text-[#68746d]">
        {occurrence
          ? "This log will complete the selected planned occurrence."
          : "This is an additional activity log. It does not complete a scheduled occurrence."}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={`Actual start (${Intl.DateTimeFormat().resolvedOptions().timeZone})`}
        >
          <input
            required
            type="datetime-local"
            className={input}
            value={values.occurred_at}
            onChange={(e) => set("occurred_at", e.target.value)}
            onInput={(e) => set("occurred_at", e.target.value)}
          />
        </Field>
        <Field label="Actual duration (minutes, optional)">
          <input
            type="number"
            min={1}
            max={1440}
            className={input}
            value={values.minutes ?? ""}
            onChange={(e) =>
              set("minutes", e.target.value ? Number(e.target.value) : null)
            }
          />
        </Field>
      </div>
      <Field
        label={
          routine.kind === "meal"
            ? "Meal / preparation label"
            : "Stage or session focus"
        }
      >
        <input
          maxLength={180}
          className={input}
          value={values.stage}
          onChange={(e) => set("stage", e.target.value)}
        />
      </Field>
      {routine.kind !== "meal" && (
        <Field label="Self-reported effort (1–10, optional)">
          <input
            type="number"
            min={1}
            max={10}
            className={input}
            value={values.effort ?? ""}
            onChange={(e) =>
              set("effort", e.target.value ? Number(e.target.value) : null)
            }
          />
        </Field>
      )}
      <Field label="What actually happened">
        <textarea
          rows={3}
          className={input}
          maxLength={5000}
          value={values.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Field>
      <div className="flex gap-2">
        <button disabled={busy} className="primary-button">
          Save activity
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RoutineDetail({ routine, timezone, busy, run, log, close }) {
  const [clock, setClock] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [moveTime, setMoveTime] = useState("");
  const [duration, setDuration] = useState(routine.duration_minutes);
  const schedule = useInfiniteQuery({
    queryKey: ["routine-schedule", routine.id, includeClosed],
    queryFn: ({ pageParam }) =>
      routineService.occurrences(routine.id, includeClosed, pageParam),
    initialPageParam: 0,
    getNextPageParam: (page) => page.next_offset ?? undefined,
  });
  const history = useInfiniteQuery({
    queryKey: ["routine-history", routine.id],
    queryFn: ({ pageParam }) => routineService.history(routine.id, pageParam),
    initialPageParam: undefined,
    getNextPageParam: (page) => page.next_cursor || undefined,
  });
  const act = (kind, item) => {
    setDecision({ kind, item });
    setReason("");
    setMoveTime("");
    setDuration(routine.duration_minutes);
  };
  return (
    <section
      className="surface-panel p-5 space-y-4"
      aria-label={`Schedule and history for ${routine.title}`}
    >
      <div className="flex justify-between gap-3">
        <h3 className="font-bold">Schedule & activity · {routine.title}</h3>
        <button className="quiet-button" onClick={close}>
          Close details
        </button>
      </div>
      <label className="flex gap-2 text-xs">
        <input
          type="checkbox"
          checked={includeClosed}
          onChange={(e) => setIncludeClosed(e.target.checked)}
        />
        Include completed, skipped and cancelled occurrences
      </label>
      {schedule.isLoading && <p role="status">Loading schedule…</p>}
      {schedule.error && (
        <p role="alert">
          Schedule unavailable.{" "}
          <button className="quiet-button" onClick={() => schedule.refetch()}>
            Retry schedule
          </button>
        </p>
      )}
      {schedule.data?.pages
        .flatMap((page) => page.items)
        .map((item) => (
          <article
            className="rounded-lg border border-[#e1e7e1] p-4"
            key={item.id}
          >
            <p className="text-sm font-semibold">
              {display(item.starts_at, timezone)} –{" "}
              {display(item.ends_at, timezone)} · {item.status}
              {item.overridden ? " · Rescheduled" : ""}
            </p>
            {item.reason && (
              <p className="mt-1 text-xs text-[#68746d]">{item.reason}</p>
            )}
            {item.conflicts.length > 0 && (
              <p className="mt-2 text-sm text-[#996617]">
                Conflicts with{" "}
                {item.conflicts.map((row) => row.title).join(", ")}.{" "}
                <Link className="underline" to="/calendar">
                  Open calendar
                </Link>
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {item.status === "planned" && (
                <>
                  <button
                    disabled={
                      busy || new Date(item.starts_at).getTime() > clock
                    }
                    className="primary-button text-xs"
                    onClick={() => log(item)}
                  >
                    Record completed activity
                  </button>
                  <button
                    className="secondary-button text-xs"
                    onClick={() => act("reschedule", item)}
                  >
                    Move this occurrence
                  </button>
                  <button
                    className="quiet-button text-xs"
                    onClick={() => act("skip", item)}
                  >
                    Skip occurrence
                  </button>
                </>
              )}
              {item.status === "skipped" && (
                <button
                  className="quiet-button text-xs"
                  onClick={() => act("reopen", item)}
                >
                  Reopen occurrence
                </button>
              )}
            </div>
          </article>
        ))}
      {schedule.data && !schedule.data.pages[0].items.length && (
        <p className="text-sm text-[#68746d]">
          No matching planned occurrences in the tracked schedule.
        </p>
      )}
      {schedule.hasNextPage && (
        <button
          className="secondary-button"
          disabled={schedule.isFetchingNextPage}
          onClick={() => schedule.fetchNextPage()}
        >
          Load more occurrences
        </button>
      )}
      {decision && (
        <form
          aria-label="Change routine record"
          className="rounded-lg border border-[#e6dfcb] bg-[#fffdf4] p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            run(async () => {
              if (decision.kind === "reschedule")
                await routineService.reschedule(decision.item.id, {
                  starts_at: new Date(moveTime).toISOString(),
                  duration_minutes: duration,
                });
              else if (decision.kind === "void")
                await routineService.void(decision.item.id, reason);
              else
                await routineService.decide(decision.item.id, {
                  action: decision.kind,
                  reason,
                });
              setDecision(null);
            });
          }}
        >
          <h4 className="font-semibold">
            {decision.kind === "reschedule"
              ? "Move this occurrence"
              : decision.kind === "void"
                ? "Void incorrect activity"
                : decision.kind === "skip"
                  ? "Skip this occurrence"
                  : "Reopen this occurrence"}
          </h4>
          <p className="text-xs text-[#68746d]">
            {decision.kind === "void"
              ? "The log stays in history but no longer counts toward progress. Its linked planned occurrence reopens."
              : decision.kind === "reschedule"
                ? "Only this occurrence moves. Timelyzer checks your saved calendar and other planned routines."
                : "This changes the plan without adding an activity or increasing your completed count."}
          </p>
          {decision.kind === "reschedule" ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={`New start (${Intl.DateTimeFormat().resolvedOptions().timeZone})`}
              >
                <input
                  className={input}
                  type="datetime-local"
                  required
                  value={moveTime}
                  onChange={(e) => setMoveTime(e.target.value)}
                  onInput={(e) => setMoveTime(e.target.value)}
                />
              </Field>
              <Field label="Duration (minutes)">
                <input
                  className={input}
                  type="number"
                  required
                  min={1}
                  max={1440}
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                />
              </Field>
            </div>
          ) : (
            <Field label="Reason">
              <input
                className={input}
                required
                minLength={3}
                maxLength={1000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          )}
          <div className="flex gap-2">
            <button disabled={busy} className="primary-button">
              {decision.kind === "void" ? "Void activity" : "Save change"}
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setDecision(null)}
            >
              Cancel change
            </button>
          </div>
        </form>
      )}
      <h4 className="pt-3 font-bold">Actual activity history</h4>
      {history.isLoading && <p role="status">Loading activity…</p>}
      {history.error && (
        <p role="alert">
          Activity history unavailable.{" "}
          <button className="quiet-button" onClick={() => history.refetch()}>
            Retry history
          </button>
        </p>
      )}
      {history.data?.pages
        .flatMap((page) => page.items)
        .map((item) => (
          <article
            className="rounded-lg border border-[#e1e7e1] p-4"
            key={item.id}
          >
            <p className="text-sm font-semibold">
              {display(item.occurred_at, timezone)}
              {item.voided ? " · Voided" : " · Recorded"}
              {item.minutes ? ` · ${item.minutes} minutes` : ""}
            </p>
            <p className="mt-1 text-sm">
              {item.stage}
              {item.effort ? ` · Effort ${item.effort}/10` : ""}
            </p>
            <p className="mt-2 whitespace-pre-wrap text-sm text-[#68746d]">
              {item.notes}
            </p>
            {item.voided ? (
              <p className="mt-2 text-xs">Correction: {item.void_reason}</p>
            ) : (
              <button
                className="quiet-button mt-2 text-xs"
                onClick={() => act("void", item)}
              >
                Correct activity
              </button>
            )}
          </article>
        ))}
      {history.data && !history.data.pages[0].items.length && (
        <p className="text-sm text-[#68746d]">
          No activity logs yet. A scheduled session does not count as attended.
        </p>
      )}
      {history.hasNextPage && (
        <button
          className="secondary-button"
          disabled={history.isFetchingNextPage}
          onClick={() => history.fetchNextPage()}
        >
          Load older activity
        </button>
      )}
    </section>
  );
}

export default function RoutineDesk({ run, busy }) {
  const query = useQuery({
    queryKey: ["routines"],
    queryFn: routineService.overview,
    refetchInterval: 60000,
  });
  const [editing, setEditing] = useState(null);
  const [logging, setLogging] = useState(null);
  const [detailId, setDetailId] = useState(null);
  if (query.isLoading) return <p role="status">Loading routines…</p>;
  if (query.error)
    return (
      <p role="alert">
        Could not load routines.{" "}
        <button className="secondary-button" onClick={() => query.refetch()}>
          Retry routines
        </button>
      </p>
    );
  const data = query.data;
  const detail = data.routines.find((row) => row.id === detailId);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">Training, meals & routines</h2>
          <p className="mt-1 text-sm text-[#68746d]">
            Plan your week, track actual activity, and prepare for the next
            milestone.
          </p>
        </div>
        <button className="primary-button" onClick={() => setEditing({})}>
          Add routine
        </button>
      </div>
      {editing && (
        <RoutineForm
          key={editing.id || "new"}
          initial={editing.id ? editing : null}
          today={data.today}
          timezone={data.timezone}
          busy={busy}
          close={() => setEditing(null)}
          save={(values) =>
            run(async () => {
              if (editing.id) await routineService.edit(editing.id, values);
              else await routineService.create(values);
              setEditing(null);
            })
          }
        />
      )}
      {logging && (
        <ActivityForm
          key={`${logging.routine.id}:${logging.occurrence?.id || "extra"}`}
          {...logging}
          busy={busy}
          close={() => setLogging(null)}
          save={(values) =>
            run(async () => {
              await routineService.log(values);
              setLogging(null);
            })
          }
        />
      )}
      {detail && (
        <RoutineDetail
          key={detail.id}
          routine={detail}
          timezone={data.timezone}
          busy={busy}
          run={run}
          close={() => setDetailId(null)}
          log={(occurrence) => setLogging({ routine: detail, occurrence })}
        />
      )}
      {!data.routines.length && (
        <div className="surface-panel p-6 text-sm text-[#68746d]">
          Add a class, meal or other routine with the days and times that work
          for you. Existing progress can be recorded as a dated starting count.
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {data.routines.map((routine) => (
          <section
            className="surface-panel p-5"
            key={routine.id}
            aria-label={routine.title}
          >
            <div className="flex justify-between gap-2">
              <h3 className="font-bold">{routine.title}</h3>
              <span className="status-pill status-pill-info">
                {routine.enabled ? routine.kind : "Paused"}
              </span>
            </div>
            <p className="mt-3 text-2xl font-bold">
              {routine.completed_count}
              <span className="ml-2 text-sm font-normal text-[#68746d]">
                completed
                {routine.target_count ? ` / ${routine.target_count} goal` : ""}
              </span>
            </p>
            <p className="mt-1 text-xs text-[#68746d]">
              {routine.baseline_count} previously reported
              {routine.baseline_as_of
                ? ` through ${routine.baseline_as_of}`
                : ""}{" "}
              · {routine.logged_count} logged · {routine.logged_minutes} logged
              minutes
            </p>
            <p className="mt-3 text-sm">
              {routine.weekdays.map((day) => days[day].slice(0, 3)).join(", ")}{" "}
              · {routine.local_time} ({data.timezone}) ·{" "}
              {routine.duration_minutes} minutes
            </p>
            {routine.goal && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-[#68746d]">
                Goal: {routine.goal}
              </p>
            )}
            {routine.plan && (
              <p className="mt-2 whitespace-pre-wrap text-sm">
                Plan: {routine.plan}
              </p>
            )}
            {routine.preferences && (
              <p className="mt-2 whitespace-pre-wrap text-xs text-[#68746d]">
                Preferences: {routine.preferences}
              </p>
            )}
            {routine.current_milestone && (
              <p className="mt-3 text-sm text-[#376653]">
                Reached: {routine.current_milestone.title} ·{" "}
                {routine.current_milestone.after_sessions} completed
                {routine.current_milestone.notes
                  ? ` · ${routine.current_milestone.notes}`
                  : ""}
              </p>
            )}
            {routine.next_milestone && (
              <div className="mt-3 rounded-lg bg-[#f2f6f3] p-3 text-sm">
                <p className="font-semibold">
                  Next: {routine.next_milestone.title} · after{" "}
                  {routine.next_milestone.after_sessions} completed
                </p>
                <p className="mt-1 text-xs">
                  {routine.next_milestone.after_sessions -
                    routine.completed_count}{" "}
                  more to record. {routine.next_milestone.notes}
                </p>
              </div>
            )}
            {routine.latest_activity && (
              <p className="mt-3 text-xs text-[#68746d]">
                Last recorded:{" "}
                {display(routine.latest_activity.occurred_at, data.timezone)} ·{" "}
                {routine.latest_activity.stage || "Activity"}
              </p>
            )}
            {!routine.schedule_caught_up && (
              <p className="mt-3 text-xs text-[#996617]">
                Schedule catch-up is continuing after an offline period.
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                className="primary-button text-xs"
                onClick={() => setDetailId(routine.id)}
              >
                Schedule & logs
              </button>
              <button
                className="secondary-button text-xs"
                onClick={() => setLogging({ routine })}
              >
                Log extra activity
              </button>
              <button
                className="quiet-button text-xs"
                onClick={() => setEditing(routine)}
              >
                Edit routine
              </button>
            </div>
            <RoutineBriefings routine={routine} />
          </section>
        ))}
      </div>
      <p className="text-xs text-[#68746d]">
        Preparation reminders and conflicts appear in Attention. Background
        monitoring checks while you are away. Plans stay separate from your
        calendar; moving a routine does not move a meeting.
      </p>
    </div>
  );
}
